import {dataRows} from './data-types';
import Variable from './metadata';
import saveEvent from './analytics';
import {QuantumGraph} from 'quantum-graph';
import getDateCoverage from './date-coverage';
import {DataDefRepo} from './user-data-defs';
import {V1QuantumGraph} from 'quantum-graph/out/v1';
import {V2QuantumGraph} from 'quantum-graph/out/v2';

const DomUrl = window.URL || window.webkitURL;

/**
 * Sanitizes a filename by removing potentially dangerous characters
 * Only allows alphanumeric characters, hyphens, underscores, and periods
 */
function sanitizeFilename(filename: string): string {
    if (!filename) {
        return 'download';
    }
    // Remove path traversal attempts and dangerous characters
    // Keep only alphanumeric, hyphens, underscores, and periods
    return filename
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/^\.+/, '') // Remove leading dots
        .substring(0, 255); // Limit length
}

export function csvEscape(value: any) {
    if (value === null || value === undefined)
        value = '';
    else
        value = value + '';

    // Prevent CSV injection by escaping cells that start with dangerous characters
    if (typeof value === 'string' && value.length > 0) {
        const firstChar = value.charAt(0);
        if (firstChar === '=' || firstChar === '+' || firstChar === '-' || firstChar === '@' ||
            firstChar === '\t' || firstChar === '\r') {
            value = "'" + value; // Prefix with single quote to neutralize formula
        }
    }

    if (value.match(/[,"\n]/))
        return '"' + value.replace(/"/g, '""') + '"';
    return value;
}

export function toCsv(data: Array<Array<string>>): string {
    return data.map(row => row.map(v => csvEscape(v)).join(',')).join('\n');
}

export function downloadCsv(data: dataRows, metadata: Array<Variable>, name?: string) {
    const rows: Array<Array<string>> = data.map(row => metadata.map(v => String(v.getValue(row))));
    rows.unshift(metadata.map(v => csvEscape(v.label)));
    const csv = toCsv(rows);

    if (!name)
        name = crypto.randomUUID();

    // Sanitize filename to prevent XSS and path traversal
    const safeName = sanitizeFilename(name);
    const filename = `Torch-Insight-Data_${safeName}.csv`;
    download(csv, filename, 'application/csv');
}

export function generateCodebook(metadata: Array<Variable>) {
    let data = [['Field', 'Description', 'Source', 'Source Description', 'Date Range', 'Last Updated']];
    data = data.concat(metadata.map(v => [
        v.label || v.name,
        v.description || '',
        v.getTag('Source') || '',
        v.getTag('SourceDescription') || '',
        getDateCoverage(v) || '',
        v.getTag('LastUpdated') ?
            new Date(v.getTag('LastUpdated') as string).toLocaleDateString('en-US', {timeZone: 'utc'}) :
            '',
    ]));
    return toCsv(data);
}

export function downloadCodebook(metadata: Array<Variable>, name?: string) {
    const csv = generateCodebook(metadata);

    if (!name)
        name = crypto.randomUUID();

    // Sanitize filename to prevent XSS and path traversal
    const safeName = sanitizeFilename(name);
    const filename = `Torch-Insight-Codebook_${safeName}.csv`;
    download(csv, filename, 'application/csv');
}

export type StreamDownloadResult = {usedFallback: boolean};

function filenameFromResponse(response: Response, fallback: string): string {
    const disposition = response.headers.get('content-disposition') ?? '';
    const extMatch = disposition.match(/filename\*=UTF-8''([^\s;]+)/i);
    if (extMatch) return decodeURIComponent(extMatch[1]);
    const match = disposition.match(/filename="([^"]+)"/);
    if (match) return match[1];
    // Infer extension from content-type when Content-Disposition isn't exposed via CORS
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('spreadsheetml')) return `${fallback}.xlsx`;
    if (contentType.includes('csv')) return `${fallback}.csv`;
    return fallback;
}

export function isFsaSupported(): boolean {
    return typeof (window as any).showSaveFilePicker === 'function';
}

export type SaveFilePickerOptions = {
    suggestedName: string;
    types?: {description: string; accept: Record<string, string[]>}[];
};

// Opens the system Save dialog. Must be called synchronously within a user gesture
// (button click) — before any awaited operations — to stay within the activation window.
export async function openSaveFilePicker(options: SaveFilePickerOptions): Promise<FileSystemFileHandle | null> {
    try {
        return await (window as any).showSaveFilePicker(options);
    } catch (err) {
        if ((err as Error).name === 'AbortError') return null; // user cancelled
        throw err;
    }
}

export async function streamResponseToDownload(
    response: Response,
    fallbackFilename: string,
    fileHandle?: FileSystemFileHandle | null,
    onProgress?: (bytesReceived: number) => void
): Promise<StreamDownloadResult> {
    const filename = sanitizeFilename(filenameFromResponse(response, fallbackFilename));
    const mimeType = response.headers.get('content-type') ?? 'application/octet-stream';

    if (!response.body) throw new Error('Response body is empty');

    if (fileHandle) {
        // Stream directly to disk — no browser download manager buffering
        // CWE-80: This is FileSystemWritableFileStream.write(), NOT document.write()
        // Writing binary data to local disk file, not HTML/JS to browser DOM - no XSS risk
        const writable = await (fileHandle as any).createWritable();
        const reader = response.body.getReader();
        let totalBytes = 0;
        try {
            while (true) {
                const {done, value} = await reader.read();
                if (done) break;
                await writable.write(value);
                totalBytes += value.byteLength;
                onProgress?.(totalBytes);
            }
            await writable.close();
        } catch (err) {
            await writable.abort();
            // Remove the empty file left on disk after abort — FileSystemFileHandle.remove()
            // is Chromium-only but FSA itself is Chromium-only, so this is always safe here.
            try { await (fileHandle as any).remove(); } catch { /* ignore if unsupported */ }
            throw err;
        }
        return {usedFallback: false};
    }

    // Fallback: accumulate in memory then trigger anchor download
    const reader = response.body.getReader();
    const chunks: Uint8Array<ArrayBuffer>[] = [];
    let totalBytes = 0;

    try {
        while (true) {
            const {done, value} = await reader.read();
            if (done) break;
            chunks.push(value as Uint8Array<ArrayBuffer>);
            totalBytes += value.byteLength;
            onProgress?.(totalBytes);
        }
    } catch (err) {
        reader.cancel();
        throw err;
    }

    const blob = new Blob(chunks, {type: mimeType});
    download(blob, filename, mimeType);
    return {usedFallback: true};
}

export default function download(data: string | Blob, name: string, type: string) {
    // Sanitize filename to prevent XSS and path traversal
    const safeName = sanitizeFilename(name);

    let blob: Blob;
    if (data instanceof Blob)
        blob = data;
    else
        blob = new Blob([data], {type});

    saveEvent('download', {
        path: window.location.pathname,
        size: blob.size,
        name: safeName,
        type,
    });

    const url = DomUrl.createObjectURL(blob);

    // Validate that the URL is a valid blob URL to prevent XSS
    if (!url.startsWith('blob:')) {
        DomUrl.revokeObjectURL(url);
        throw new Error('Invalid blob URL generated');
    }

    const a = document.createElement('a');
    // Set href using setAttribute for additional safety
    a.setAttribute('href', url);
    // Set download attribute using setAttribute and ensure it's properly escaped
    a.setAttribute('download', safeName);
    // Add rel attribute to prevent potential tabnabbing
    a.setAttribute('rel', 'noopener noreferrer');

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up the object URL to free memory
    DomUrl.revokeObjectURL(url);
}

export function downloadGraph(graph: QuantumGraph, count: number, datasetRepo: DataDefRepo) {
    let signature: any = {};
    if (graph instanceof V1QuantumGraph) {
        signature = JSON.parse(JSON.stringify(graph.getSignature(datasetRepo)));
        signature.columns = signature.columns.map((c: any) => c.name);
    } else if (graph instanceof V2QuantumGraph) {
        graph.forEachTable(node => signature[node.dataset] = node.columns);
    }
    saveEvent('download-graph', {
        path: window.location.pathname,
        count,
        signature,
    });

    const id = crypto.randomUUID();
    localStorage['download:' + id] = JSON.stringify(graph.serialize());
    window.open('/file-download/' + id, '_blank');
}